import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Stack,
  ToggleButtonGroup,
  Button,
  LinearProgress,
} from '@mui/joy'
import { ResponsiveTable } from '../../../components/common/ResponsiveTable'
import Grid from '@mui/joy/Grid'
import {
  TrendUp as TrendingUp,
  TrendDown as TrendingDown,
  Trophy as EmojiEvents,
  ArrowUp as ArrowUpward,
  ArrowDown as ArrowDownward,
  Minus as Remove,
} from '@phosphor-icons/react'
import { useSalesLeaderboard } from '../../../hooks/useSalesReports'
import type { LeaderboardPeriodType, LeaderboardRanking } from '../../../types/salesReports'

const getInitials = (name: string) => {
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

// Medal colors for podium
const MEDAL_COLORS = {
  gold: '#FFD700',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
}

export default function SalesLeaderboard() {
  const [period, setPeriod] = useState<LeaderboardPeriodType>('this_week')

  const { data, isLoading, error } = useSalesLeaderboard({ period })

  const renderChange = (change: number | null) => {
    if (change === null) {
      return null  // Hide chip when no comparison data
    }

    const isPositive = change > 0
    const Icon = isPositive ? TrendingUp : TrendingDown
    const color = isPositive ? 'success' : 'danger'

    return (
      <Chip
        size="sm"
        variant="soft"
        color={color}
        startDecorator={<Icon size={14} />}
      >
        {isPositive ? '+' : ''}{change.toFixed(1)}%
      </Chip>
    )
  }

  const renderRankChange = (change: number) => {
    if (change === 0) {
      return <Remove color="var(--joy-palette-neutral-400)" size={16} />
    }
    if (change > 0) {
      return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <ArrowUpward color="var(--joy-palette-success-500)" size={16} />
          <Typography level="body-xs" sx={{ color: 'success.500' }}>{change}</Typography>
        </Stack>
      )
    }
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <ArrowDownward color="var(--joy-palette-danger-500)" size={16} />
        <Typography level="body-xs" sx={{ color: 'danger.500' }}>{Math.abs(change)}</Typography>
      </Stack>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert color="danger">
        Failed to load leaderboard data. Please try again.
      </Alert>
    )
  }

  if (!data || !data.rankings || data.rankings.length === 0) {
    return (
      <Alert color="warning">
        No leaderboard data available. Make sure you have active users with sales activity.
      </Alert>
    )
  }

  const { team_totals, rankings } = data

  // Separate podium (top 3) from rest
  const podium = rankings.slice(0, 3)
  const restOfRankings = rankings.slice(3)

  // Max values for progress bars
  const maxKnocks = Math.max(...rankings.map(r => r.knocks), 1)
  const maxOpps = Math.max(...rankings.map(r => r.opportunities_generated), 1)
  const maxRevenue = Math.max(...rankings.map(r => r.won_revenue), 1)

  return (
    <Box>
      {/* Period Selector */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Typography level="title-md">Time Period</Typography>
            <ToggleButtonGroup
              value={period}
              onChange={(_e, newValue) => {
                if (newValue) setPeriod(newValue as LeaderboardPeriodType)
              }}
              sx={{
                flexWrap: 'wrap',
                '& > button': { flex: { xs: '1 1 30%', sm: '0 0 auto' } },
              }}
            >
              <Button value="today" variant={period === 'today' ? 'solid' : 'outlined'}>Today</Button>
              <Button value="yesterday" variant={period === 'yesterday' ? 'solid' : 'outlined'}>Yesterday</Button>
              <Button value="this_week" variant={period === 'this_week' ? 'solid' : 'outlined'}>This Week</Button>
              <Button value="last_week" variant={period === 'last_week' ? 'solid' : 'outlined'}>Last Week</Button>
              <Button value="this_month" variant={period === 'this_month' ? 'solid' : 'outlined'}>This Month</Button>
              <Button value="last_month" variant={period === 'last_month' ? 'solid' : 'outlined'}>Last Month</Button>
            </ToggleButtonGroup>
          </Stack>
        </CardContent>
      </Card>

      {/* Team Totals Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid xs={12} sm={4}>
          <Card variant="soft" color="primary">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Total Knocks</Typography>
              <Typography level="h2">{team_totals.total_knocks}</Typography>
              {renderChange(team_totals.knocks_change)}
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} sm={4}>
          <Card variant="soft" color="warning">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Total Opportunities</Typography>
              <Typography level="h2">{team_totals.total_opportunities}</Typography>
              {renderChange(team_totals.opportunities_change)}
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} sm={4}>
          <Card variant="soft" color="success">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Total Revenue</Typography>
              <Typography level="h2">{formatCurrency(team_totals.total_revenue)}</Typography>
              {renderChange(team_totals.revenue_change)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Podium Display (Top 3) — desktop only; the rankings table below already
          shows top-3 medals inline, so we hide the podium on phones to save space. */}
      {podium.length > 0 && (
        <Card variant="outlined" sx={{ mb: 4, display: { xs: 'none', md: 'block' } }}>
          <CardContent>
            <Typography level="h4" sx={{ mb: 3, textAlign: 'center' }} startDecorator={<EmojiEvents color="var(--joy-palette-warning-400)" />}>
              Top Performers
            </Typography>

            <Stack
              direction="row"
              justifyContent="center"
              alignItems="flex-end"
              spacing={3}
              sx={{ minHeight: 280 }}
            >
              {/* 2nd Place */}
              {podium.length >= 2 && (
                <Box sx={{ textAlign: 'center', width: 180 }}>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      mx: 'auto',
                      mb: 1,
                      border: 3,
                      borderColor: MEDAL_COLORS.silver,
                    }}
                  >
                    {getInitials(podium[1].name)}
                  </Avatar>
                  <Typography level="title-md">{podium[1].name}</Typography>
                  <Typography level="h3" sx={{ color: 'text.secondary' }}>{podium[1].composite_score.toFixed(0)}</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>points</Typography>
                  <Box
                    sx={{
                      height: 100,
                      bgcolor: MEDAL_COLORS.silver,
                      borderRadius: 'sm',
                      mt: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography level="h1" sx={{ color: 'white' }}>2</Typography>
                  </Box>
                </Box>
              )}

              {/* 1st Place */}
              {podium.length >= 1 && (
                <Box sx={{ textAlign: 'center', width: 200 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 1,
                      border: 4,
                      borderColor: MEDAL_COLORS.gold,
                    }}
                  >
                    {getInitials(podium[0].name)}
                  </Avatar>
                  <Typography level="title-lg">{podium[0].name}</Typography>
                  <Typography level="h2" sx={{ color: 'warning.500' }}>{podium[0].composite_score.toFixed(0)}</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>points</Typography>
                  <Box
                    sx={{
                      height: 140,
                      bgcolor: MEDAL_COLORS.gold,
                      borderRadius: 'sm',
                      mt: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography level="h1" sx={{ color: 'white', fontSize: '3rem' }}>1</Typography>
                  </Box>
                </Box>
              )}

              {/* 3rd Place */}
              {podium.length >= 3 && (
                <Box sx={{ textAlign: 'center', width: 180 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      mx: 'auto',
                      mb: 1,
                      border: 3,
                      borderColor: MEDAL_COLORS.bronze,
                    }}
                  >
                    {getInitials(podium[2].name)}
                  </Avatar>
                  <Typography level="title-md">{podium[2].name}</Typography>
                  <Typography level="h3" sx={{ color: 'text.secondary' }}>{podium[2].composite_score.toFixed(0)}</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>points</Typography>
                  <Box
                    sx={{
                      height: 70,
                      bgcolor: MEDAL_COLORS.bronze,
                      borderRadius: 'sm',
                      mt: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography level="h1" sx={{ color: 'white' }}>3</Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Full Rankings Table */}
      <Card variant="outlined">
        <CardContent>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Full Rankings
          </Typography>
          <ResponsiveTable<LeaderboardRanking>
            rows={rankings}
            getRowKey={(r) => r.user_id}
            columns={[
              {
                key: 'rank',
                sortValue: (row) => row.rank,
                header: 'Rank',
                width: '60px',
                mobileVariant: 'header',
                render: (ranking) => (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {ranking.rank <= 3 ? (
                      <EmojiEvents
                        color={
                          ranking.rank === 1
                            ? MEDAL_COLORS.gold
                            : ranking.rank === 2
                              ? MEDAL_COLORS.silver
                              : MEDAL_COLORS.bronze
                        }
                        size={20}
                      />
                    ) : (
                      <Typography level="body-md" fontWeight="lg">#{ranking.rank}</Typography>
                    )}
                    <Avatar size="sm">{getInitials(ranking.name)}</Avatar>
                    <Typography level="title-sm">{ranking.name}</Typography>
                  </Stack>
                ),
              },
              {
                key: 'knocks',
                sortValue: (row) => row.knocks,
                header: 'Knocks',
                width: '150px',
                render: (ranking) => (
                  <Box sx={{ minWidth: 100 }}>
                    <Typography level="body-sm">{ranking.knocks}</Typography>
                    <LinearProgress
                      determinate
                      value={(ranking.knocks / maxKnocks) * 100}
                      color="primary"
                      sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                    />
                  </Box>
                ),
              },
              {
                key: 'opportunities_generated',
                sortValue: (row) => row.opportunities_generated,
                header: 'Opportunities',
                width: '150px',
                render: (ranking) => (
                  <Box sx={{ minWidth: 100 }}>
                    <Typography level="body-sm">{ranking.opportunities_generated}</Typography>
                    <LinearProgress
                      determinate
                      value={(ranking.opportunities_generated / maxOpps) * 100}
                      color="warning"
                      sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                    />
                  </Box>
                ),
              },
              {
                key: 'won_revenue',
                sortValue: (row) => Number(row.won_revenue),
                header: 'Revenue',
                width: '150px',
                render: (ranking) => (
                  <Box sx={{ minWidth: 100 }}>
                    <Typography level="body-sm">{formatCurrency(ranking.won_revenue)}</Typography>
                    <LinearProgress
                      determinate
                      value={(ranking.won_revenue / maxRevenue) * 100}
                      color="success"
                      sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                    />
                  </Box>
                ),
              },
              {
                key: 'composite_score',
                sortValue: (row) => row.composite_score,
                header: 'Score',
                width: '100px',
                render: (ranking) => (
                  <Chip
                    size="sm"
                    variant="solid"
                    color={ranking.rank <= 3 ? 'warning' : 'neutral'}
                  >
                    {ranking.composite_score.toFixed(1)}
                  </Chip>
                ),
              },
              {
                key: 'rank_change',
                header: 'Change',
                width: '60px',
                render: (ranking) => renderRankChange(ranking.rank_change),
              },
            ]}
          />
        </CardContent>
      </Card>

      {/* Score Explanation */}
      <Card variant="soft" color="neutral" sx={{ mt: 3 }}>
        <CardContent>
          <Typography level="title-sm" sx={{ mb: 1 }}>
            How Scores Are Calculated
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            The composite score is calculated using: <strong>Knocks (20%)</strong> + <strong>Opportunities (35%)</strong> + <strong>Revenue (45%)</strong>.
            The knocks component uses <strong>goal achievement percentage</strong> for users with weekly targets set — a rep at 100% of their goal scores higher than a rep at 50% of a larger goal. Users without targets are normalized against the team's top performer.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}
